import { DOCUMENT } from '@angular/common';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import {
  DialogComponent,
  IDialogComponentData,
} from 'ngx-mt-ui-lib/components/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  defer,
  EMPTY,
  exhaustMap,
  filter,
  fromEvent,
  merge,
  of,
  take,
  tap,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(Dialog);
  private readonly document = inject(DOCUMENT);
  private readonly swUpdate = inject(SwUpdate);
  private readonly translateService = inject(TranslateService);
  private started = false;
  private updateReady = false;
  private updatePromptDismissed = false;
  private waitingForDialogs = false;
  private updateDialogRef: DialogRef<boolean, DialogComponent> | null = null;

  public start(): void {
    if (this.started || !this.swUpdate.isEnabled) {
      return;
    }

    this.started = true;
    this.swUpdate.versionUpdates
      .pipe(
        filter(
          (event): event is VersionReadyEvent => event.type === 'VERSION_READY',
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.markUpdateReady());

    const foreground$ = fromEvent(this.document, 'visibilitychange').pipe(
      filter(() => this.document.visibilityState === 'visible'),
      tap(() => this.handleForeground()),
    );

    merge(of(null), foreground$)
      .pipe(
        exhaustMap(() =>
          defer(() => this.swUpdate.checkForUpdate()).pipe(
            catchError(() => EMPTY),
          ),
        ),
        filter((updateFound) => updateFound),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.markUpdateReady());
  }

  private markUpdateReady(): void {
    this.updateReady = true;
    this.openUpdateDialog();
  }

  private handleForeground(): void {
    this.updatePromptDismissed = false;
    this.openUpdateDialog();
  }

  private openUpdateDialog(): void {
    if (
      !this.updateReady ||
      this.updatePromptDismissed ||
      this.updateDialogRef
    ) {
      return;
    }

    if (this.dialog.openDialogs.length) {
      this.waitUntilDialogsClose();
      return;
    }

    const ref = this.dialog.open<
      boolean,
      IDialogComponentData,
      DialogComponent
    >(DialogComponent, {
      disableClose: true,
      ariaLabel: this.translateService.instant('PWA_UPDATE.TITLE'),
      data: {
        title: 'PWA_UPDATE.TITLE',
        content: 'PWA_UPDATE.CONTENT',
        width: '28.75rem',
        buttons: [
          {
            title: 'GENERAL.CANCEL',
            styledSeverity: 'severity-secondary',
            onClick: () => false,
          },
          {
            title: 'PWA_UPDATE.ACTION',
            styledEmphasis: 'emphasis-filled',
            onClick: () => this.reload(),
          },
        ],
      },
    });
    this.updateDialogRef = ref;

    ref.closed.pipe(take(1)).subscribe((updateAccepted) => {
      if (this.updateDialogRef === ref) {
        this.updateDialogRef = null;
      }

      if (updateAccepted === true) {
        this.updateReady = false;
        this.updatePromptDismissed = false;
      } else {
        this.updatePromptDismissed = true;
      }
    });
  }

  private waitUntilDialogsClose(): void {
    if (this.waitingForDialogs) {
      return;
    }

    this.waitingForDialogs = true;
    this.dialog.afterAllClosed
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.waitingForDialogs = false;
        queueMicrotask(() => this.openUpdateDialog());
      });
  }

  private reload(): boolean {
    this.document.defaultView?.location.reload();
    return true;
  }
}

export function pwaUpdateInitializer(): void {
  inject(PwaUpdateService).start();
}
